import React from 'react';

import { Dropdown, DropdownOption } from '../../../ui-kit/Dropdown/Dropdown';
import { useQuery } from '@apollo/client';
import { GET_CARVES } from '../../../query/getCarves';
import { GetCarves } from '../../../query/__generated__/GetCarves';

export interface CarveOption {
  id: string;
  name: string;
  filterCount: number;
  carveFilters: Array<{
    field_name: any;
    operator: any;
    operand: string | null;
    operandList: string[] | null;
  }>;
}

interface CarveDropdownProps {
  dealId: string;
  onValueChange?: (carve: CarveOption | null) => void;
  value?: string; // Current selected carve ID for controlled behavior
  disabled?: boolean;
  className?: string;
  allowFiltering?: boolean;
}

export const CarveDropdown: React.FC<CarveDropdownProps> = ({
  dealId,
  onValueChange,
  value,
  disabled = false,
  className,
  allowFiltering,
}) => {
  // Get all carves for this deal
  const { data: carveData, loading: carveLoading } = useQuery<GetCarves>(GET_CARVES, {
    variables: {
      deal_id: dealId,
      // No bid_id - get all carves for the deal
    },
  });

  // Don't render dropdown if filtering is not allowed or if we don't have the data yet
  if (allowFiltering !== true) {
    onValueChange?.(null);
    return null;
  }

  const carves: CarveOption[] = (carveData?.deal?.carves || [])
    .filter((carve): carve is NonNullable<typeof carve> => carve != null)
    .map((carve) => {
      const carveFilters = (carve.carve || []).map((filter) => ({
        field_name: filter.field_name,
        operator: filter.operator,
        operand: filter.operand,
        operandList: filter.operandList,
      }));

      return {
        id: carve.id,
        name: carve.name,
        filterCount: carveFilters.length,
        carveFilters,
      };
    });

  const dropdownOptions: DropdownOption[] = carves.map((carve) => ({
    value: carve.id,
    label: carve.name,
  }));

  const handleValueChange = (option: DropdownOption) => {
    const selectedCarve = carves.find((carve) => carve.id === option.value);
    if (selectedCarve) {
      onValueChange?.(selectedCarve);
    }
  };

  const handleClear = () => {
    onValueChange?.(null);
  };

  const selectedOption = dropdownOptions.find((opt) => opt.value === value);

  return (
    <div className="flex flex-col">
      <label className="mb-1 text-sm text-gray-400">Carve Selection</label>
      <Dropdown
        options={dropdownOptions}
        value={selectedOption?.label || ''}
        placeholder={'Select carve...'}
        onValueChange={handleValueChange}
        onClear={handleClear}
        showClear={true}
        variant="gray"
        disabled={disabled || carveLoading}
        className={className}
      />
    </div>
  );
};
